<template>
  <form id="searchForm" @submit.prevent="search">
    <div class="d-flex align-items-center justify-content-lg-between mb-3">

      <div class="row">

        <div class="col">
          <div class="input-group search-group">
            <button id="btnSearch" class="btn btn-primary" type="button" @click="search">
              <img src="@/assets/img/icons/magnifying-glass.svg" class="input-left-icon clickable" alt="search">
            </button>
            <input
                type="text"
                id="textFilter"
                class="form-control w-484"
                autofocus
                :placeholder="$t('adm.page.users.textFilterPlaceholder')"
                v-model="searchForm.$search"
                :aria-label="$t('adm.page.users.textFilterPlaceholder')"
                aria-describedby="btnSearch"
                @keyup.enter="search">
          </div>
        </div>

        <div class="col" v-if="myRole === 'superAdmin' || myRole === 'orgSuperAdmin'">
          <multiselect
              id="countryFilter"
              v-model="searchForm.countries"
              :options="countryOptions"
              value-prop="key"
              label="label"
              mode="single"
              :close-on-select="true"
              :can-deselect="true"
              :can-clear="true"
              :searchable="true"
              @keyup.enter="search"
              :placeholder="$t('adm.label.country', 2)"/>
        </div>

        <div class="col">
          <select-multi-organizations
              id="orgFilter"
              :selected="searchForm.organizations"
              :options-provider="fetchSelectedOrganizations"
              :selection-format-supplier="(c) => `${$t('adm.label.organization')} (${c})`"
              @keyup.enter="search"/>
        </div>

        <div class="col" v-if="myRole === 'orgSuperAdmin' || myRole === 'superAdmin'">
          <stihl-multiselect
              id="adminLevel"
              v-model="searchForm.adminLevel"
              @keyup.enter="search"
              :selection-format-supplier="(c) => `${$t('adm.label.adminLevel')} (${c})`"
              :options-provider="async () => adminLevelOptions"/>
        </div>

        <div class="col" v-if="myRole === 'orgSuperAdmin' || myRole === 'superAdmin'">
          <stihl-multiselect
              id="syncFlag"
              class=""
              v-model="searchForm.syncFlag"
              :options-provider="async () => syncOptions"
              :selection-format-supplier="(c) => `${$t('adm.label.syncFlagShort')} (${c})`"
              @keyup.enter="search"/>
        </div>

      </div>

      <div class="btn-group" role="group">
        <the-spinner v-if="dlActive" class="small-icon"/>
        <button
            id="btnDownload"
            type="button"
            class="btn link-with-icon"
            @click="downloadRequested = true"
            :disabled="dlActive"
            v-if="myRole === 'orgSuperAdmin' || myRole === 'superAdmin'">
          <img src="@/assets/img/icons/arrow-down-line.svg" alt="download"> {{ $t('adm.page.users.downloadExcel') }}
        </button>
        <button id="btnAddUser" type="button" class="btn btn-primary" @click="gotoAddUserPage">
          {{ $t('adm.button.addUser') }}
        </button>
      </div>

    </div>

    <div class="row mb-3">
      <div class="col">
        <div class="form-check form-check-inline">
          <input
              id="r-all"
              name="rgVerified"
              type="radio"
              class="form-check-input"
              value=""
              v-model="searchForm.verified">
          <label class="form-check-label" for="r-all">{{ $t('adm.page.users.all') }}</label>
        </div>
        <div class="form-check form-check-inline">
          <input
              id="r-unverified"
              name="rgVerified"
              type="radio"
              class="form-check-input"
              value="true"
              v-model="searchForm.verified">
          <label class="form-check-label" for="r-unverified">{{ $t('adm.page.users.verifiedOnly') }}</label>
        </div>
        <div class="form-check form-check-inline">
          <input
              id="r-verified"
              name="rgVerified"
              type="radio"
              class="form-check-input"
              value="false"
              v-model="searchForm.verified">
          <label class="form-check-label" for="r-verified">{{ $t('adm.page.users.unverifiedOnly') }}</label>
        </div>
      </div>
    </div>

    <confirmation-modal
        id="c-download"
        v-if="downloadRequested"
        :title="$t('adm.page.users.confirmDownload')"
        @cancel="downloadRequested = false"
        @confirm="download"/>

  </form>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Backend, { AdminLevel, adminLevels, SyncFlag, syncFlags, UserFilter, } from '@/components/backend';
import { arraySize, compareStr, contains, DisplayableOrg, getFileName, } from '@/components/utils';
import { Logger, UserProfile } from 'oidc-client-ts';
import StihlMultiselect from '@/components/ui/StihlMultiselect.vue';
import { i18n, supportedCountries } from '@/lang';
import { mapState, mapWritableState } from 'pinia';
import useAppStore from '@/components/store';
import { LocationQueryRaw } from 'vue-router';
import TheSpinner from '@/components/ui/TheSpinner.vue';
import useUserStore from '@/components/store/users.store';
import Multiselect from '@vueform/multiselect';
import ConfirmationModal from '@/components/ui/ConfirmationModal.vue';
import SelectMultiOrganizations, { toDisplayable } from '@/components/ui/SelectMultiOrganizations.vue';
import { isValidID, sanitize, sanitize1val } from '@/components/sanitizers';

type CountryCode = string;

export function isAdminLevel(x: any): x is AdminLevel {
  return typeof x === 'string' && contains(adminLevels, x);
}

export function isValidCountry(x: any): x is CountryCode {
  return typeof x === 'string' && /^[a-zA-Z]{3}$/.test(x) && contains(supportedCountries(), x as string);
}

export function isSyncFlag(x: any): x is SyncFlag {
  return typeof x === 'string' && contains(syncFlags, x);
}

export function isVerifiedFlag(x: any): x is '' | 'true' | 'false' {
  return x === '' || x === 'true' || x === 'false';
}

export default defineComponent({
  name: 'UsersFilter',
  components: {
    SelectMultiOrganizations,
    ConfirmationModal,
    TheSpinner,
    StihlMultiselect,
    Multiselect,
  },
  emits: ['usersSearch'],
  data() {
    return {
      searchForm: {
        $search: this.$route.query.text ? decodeURIComponent(this.$route.query.text as string) : '',
        countries: sanitize(this.$route.query.c, [], isValidCountry),
        organizations: sanitize(this.$route.query.org, [], isValidID), // organization IDs
        adminLevel: sanitize(this.$route.query.adm, [], isAdminLevel),
        syncFlag: sanitize(this.$route.query.sync, [], isSyncFlag),
        verified: sanitize1val(this.$route.query.verified, isVerifiedFlag, ''),
      },
      downloadRequested: false,
    };
  },
  computed: {
    // inject state getters
    ...mapState(useAppStore, {
      myRole: 'getMyAdminRole',
    }),
    ...mapWritableState(useUserStore, {
      dlActive: 'exportActive',
    }),
    syncOptions() {
      return [
        { key: 'undecided', label: this.$t('adm.label.syncFlags.unknown') },
        { key: 'sync', label: this.$t('adm.label.syncFlags.active') },
        { key: 'block', label: this.$t('adm.label.syncFlags.inactive') },
      ];
    },
    adminLevelOptions() {
      return [
        { key: 'none', label: this.$t('adm.label.adminRoles.none') },
        { key: 'userAdmin', label: this.$t('adm.label.adminRoles.userAdmin') },
        { key: 'orgSuperAdmin', label: this.$t('adm.label.adminRoles.orgSuperAdmin') },
        { key: 'superAdmin', label: this.$t('adm.label.adminRoles.superAdmin') },
      ];
    },
    countryOptions() {
      return supportedCountries()
          .map((c) => ({ key: c, label: this.$t(`adm.countries.${c}`) }))
          .sort((a, b) => compareStr(a.label, b.label));
    },
  },
  methods: {

    organizationFromToken(p: UserProfile): DisplayableOrg[] {
      if (!p) return [];
      const orgId = p.dealer_org_id as string;
      const orgName = p.dealer_org_name as string;
      const crmNo = p.dealer_org_account_number as string;
      return [new DisplayableOrg({
        id: orgId,
        name: orgName,
        crmNo,
      })];
    },

    async fetchSelectedOrganizations(): Promise<DisplayableOrg[]> {
      if (arraySize(this.searchForm.organizations) > 0) {
        const $filter = this.searchForm.organizations?.map((o) => `id == '${o}'`).join(' or ');
        Logger.info('users', `search org by "${$filter}"`);
        const api = await Backend.validateToken();
        return api.findOrganizations({ $filter, $select: 'id,name,crmNo,state' })
            .then((r) => toDisplayable(r.data));
      }
      return [];
    },

    search() {
      this.updateURLQuery();
      this.$emit('usersSearch', this.buildUserFilter());
    },

    download() {
      if (this.dlActive) return; // download already started

      this.updateURLQuery();

      const filter = this.buildUserFilter();

      this.dlActive = true;

      Backend.validateToken()
          .then((api) => api
              .downloadUsers(filter ?? {})
              .then(async (resp: Response) => {
                const filename = getFileName(resp.headers.get('content-disposition'), 'dealers.zip');
                const blob = await resp.blob();
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = filename;
                link.click();

                // some say this is important too, or it may be unnecessarily spiking memory!
                setTimeout(() => window.URL.revokeObjectURL(link.href), 0);
              })
              .catch(() => {
                this.$toast.error(this.$t('adm.page.users.downloadError'));
              }))
          .finally(() => {
            this.dlActive = false;
          });
    },

    buildUserFilter() {
      return {
        $search: this.searchForm.$search,
        'countries[]': this.searchForm.countries,
        'organizations[]': this.searchForm.organizations,
        'adminLevel[]': this.searchForm.adminLevel,
        'syncFlag[]': this.searchForm.syncFlag,
        verified: this.searchForm.verified ? (this.searchForm.verified === 'true') : undefined,
      } as UserFilter;
    },

    /**
     * update the current URL to reflect the filter values
     */
    updateURLQuery() {
      Logger.debug('UsersFilter', 'updating URL...');

      interface UsersQuery extends LocationQueryRaw {
        text?: string,
        org?: string[],
        adm?: string[],
        sync?: string[],
      }

      const query = {} as UsersQuery;
      if (this.searchForm.$search) query.text = encodeURIComponent(this.searchForm.$search);
      if (this.searchForm.countries !== query.c) query.c = this.searchForm.countries;
      if (this.searchForm.organizations !== query.org) query.org = this.searchForm.organizations;
      if (this.searchForm.adminLevel !== query.adm) query.adm = this.searchForm.adminLevel;
      if (this.searchForm.syncFlag !== query.sync) query.sync = this.searchForm.syncFlag;
      if (this.searchForm.verified) query.verified = this.searchForm.verified;

      Logger.debug('UsersFilter', 'new query:', JSON.stringify(query));
      this.$router.replace({ query });
    },

    gotoAddUserPage() {
      // this.$router.push(`/:locale/members/create/${this.organization.id}`);
      this.$router.push(`/${i18n.global.locale}/members/create`);
    },

  },
  mounted() {
    // fill form from query-params
    Logger.debug('UsersFilter', 'mounting with params: ', this.$route.query);
    // trigger the initial search
    this.search();
  },
});
</script>

<style scoped>
.search-group {
  width: 30rem;
}

#btnSearch {
  z-index: 10;
}

</style>
